<template>
    <div>
        <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
            <page-header
                :subtitle="selectedCampaign.name"
                :title="$t('global.mainNav.campaigns')"
                :to="{ name: 'campaigns' }"
            >
                <nio-button class="hover:text-red-500" icon="regular/bin-2-alternate" @click="promptDeleteCampaign">
                    {{ $t('global.general.delete') }}
                </nio-button>

                <nio-button icon="regular/pencil-write" @click="triggerEdit">
                    {{ $t('global.general.edit') }}
                </nio-button>

                <nio-button
                    icon="regular/add"
                    tag="router-link"
                    :to="{ name: 'campaigns.import.upload', params: { campaignId: $route.params.id } }"
                >
                    {{ $t('campaigns.contactListing.importContacts') }}
                </nio-button>
                <div v-tooltip="missingValidProviderPhoneTooltip()">
                    <nio-button
                        color="blue"
                        :disabled="!selectedCampaign.contacts_count || !userValidProviderPhones"
                        icon="regular/send-email-envelope"
                        @click="triggerSendCampaign"
                    >
                        {{ $t('campaigns.contactListing.sendCampaign') }}
                    </nio-button>
                </div>
            </page-header>

            <content-box :loading="$wait.is(['fetching.campaign', 'fetching.campaignContacts'])">
                <template #header>
                    <content-box-tabs :active-tab.sync="filters.tab" :tabs="tabs" />
                    <content-box-search
                        :disabled="!selectedCampaign.contacts_count"
                        :placeholder="$t('contacts.contactsList.search')"
                        v-model="filters.search"
                    />
                </template>
                <template #body>
                    <nio-table
                        :columns="columns"
                        :rows="contacts"
                        :selections.sync="selections"
                        ref="body"
                    >
                        <!-- <div class="flex h-full justify-center items-center" v-if="!pagination.total">
                            {{ selectedCampaign.contacts_count ? $t('campaigns.contactListing.noContactsInCampaign') : $t('campaigns.contactListing.noMatchingContacts') }}
                        </div> -->
                        <template #no-results>
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>
                            <p class="mb-2 text-xl">
                                {{ $t('campaigns.contactListing.noContactsInCampaign') }}
                            </p>
                            <p class="mb-4">
                                {{ $t('campaigns.contactListing.wouldYouLikeToAddOne') }}
                            </p>
                            <nio-button
                                color="blue"
                                icon="regular/add"
                                tag="router-link"
                                :to="{ name: 'campaigns.import.upload', params: { campaignId: $route.params.id } }"
                            >
                                {{ $t('campaigns.contactListing.importContacts') }}
                            </nio-button>
                        </template>
                        <template v-slot:[`column-${column.name}`]="{ row: contact }" v-for="column in columns">
                            <campaign-contact-list-item
                                :column="column.name"
                                :contact="contact"
                                :truncate="column.truncate"
                                :key="column.name"
                                @delete="triggerDeleteContact(contact)"
                            />
                        </template>
                    </nio-table>
                </template>
                <template #footer>
                    <div class="flex-1 flex items-center justify-start p-4">
                        <nio-dropdown :disabled="!selectedContacts.length" placement="top-start">
                            <template #trigger>
                                <nio-button color="blue">
                                    {{ $t('contacts.contactsList.bulkAction') }}
                                    <nio-icon class="text-xs ml-2" name="regular/arrow-up-1" />
                                </nio-button>
                            </template>

                            <nio-dropdown-item
                                class="hover:text-red-500"
                                icon="regular/bin-2-alternate"
                                @click="triggerRemoveContacts"
                            >
                                {{ $t('campaigns.contactListing.actions.removeFromCampaign') }}
                            </nio-dropdown-item>
                        </nio-dropdown>
                    </div>
                    <div class="flex-1 flex items-center justify-center p-4">
                        <pagination
                            :current-page="pagination.currentPage"
                            :last-page="pagination.lastPage"
                            @update="onPageChanged"
                            v-if="pagination.total"
                        />
                    </div>
                    <div class="flex-1 flex items-center justify-end p-4">
                        <pagination-info
                            :current="pagination.currentPage"
                            :per-page="pagination.perPage"
                            :total="pagination.total"
                        />
                    </div>
                </template>
            </content-box>
        </div>

        <campaign-modal />
        <send-mass-sms-contacts-modal @confirm="reset" />
        <confirm-modal
            :opened.sync="modals.deleteCampaignOpened"
            :title="$tc('campaigns.deleteModal.deleteTitle', 1)"
            @confirm="triggerDeleteCampaign"
        >
            <p slot="message">
                {{ $tc('campaigns.deleteModal.deleteMessage', 1) }}
            </p>
        </confirm-modal>
        <confirm-modal
            :opened.sync="modals.removeContactOpened"
            :title="$t('campaigns.contactListing.removeFromCampaignModal.title')"
            @confirm="deleteSelected"
            @deny="toDelete = null"
        >
            <p slot="message">
                {{ $tc('campaigns.contactListing.removeFromCampaignModal.message', selectedContacts.length || 1, { number: selectedContacts.length }) }}
            </p>
        </confirm-modal>

        <filters-in-url :filters="filters" />
    </div>
</template>

<script>
    import debounce from 'lodash/debounce.js';
    import { mapState, mapActions, mapMutations } from 'vuex';
    import CampaignContactListItem from '@/views/Campaigns/CampaignContactListItem.vue';
    import CampaignModal from '@/components/Modals/CampaignModal.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ContentBoxSearch from '@/components/ContentBoxSearch.vue';
    import ContentBoxTabs from '@/components/ContentBoxTabs.vue';
    import FiltersInUrl from '@/components/Utils/FiltersInUrl.js';
    import PageHeader from '@/components/PageHeader.vue';
    import Pagination from '@/components/Pagination.vue';
    import PaginationInfo from '@/components/PaginationInfo.vue';
    import SendMassSmsContactsModal from '@/components/Modals/SendMassSmsContactsModal.vue';

    export default {
        components: {
            CampaignContactListItem,
            CampaignModal,
            ConfirmModal,
            ContentBox,
            ContentBoxSearch,
            ContentBoxTabs,
            FiltersInUrl,
            PageHeader,
            Pagination,
            PaginationInfo,
            SendMassSmsContactsModal,
        },

        data() {
            return {
                contacts: [],
                selectedContacts: [],
                filters: {
                    search: '',
                    tab: 'all',
                },
                selections: [],
                toDelete: null,
                modals: {
                    deleteCampaignOpened: false,
                    removeContactOpened: false,
                },
                pagination: {
                    currentPage: 1,
                    from: 0,
                    lastPage: 0,
                    perPage: 25,
                    to: 0,
                    total: 0,
                },
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('campaign', [
                'selectedCampaign',
            ]),

            sentFilter() {
                if (this.filters.tab == 'sent') {
                    return true;
                }

                if (this.filters.tab == 'unsent') {
                    return false;
                }

                return null;
            },

            withReplyFilter() {
                if (this.filters.tab == 'withReply') {
                    return true;
                }

                if (this.filters.tab == 'withoutReply') {
                    return false;
                }

                return null;
            },

            tabs() {
                return [
                    {
                        value: 'all',
                        text: `${this.$t('campaigns.contactListing.tabs.all')}`,
                        icon: 'regular/navigation-menu',
                    },
                    {
                        value: 'sent',
                        text: `${this.$t('campaigns.contactListing.tabs.sent')}`,
                        icon: 'regular/check-circle-1',
                    },
                    {
                        value: 'unsent',
                        text: `${this.$t('campaigns.contactListing.tabs.unsent')}`,
                        icon: 'regular/remove-circle',
                    },
                    {
                        value: 'withReply',
                        text: `${this.$t('campaigns.contactListing.tabs.withReply')}`,
                        icon: 'regular/messages-bubble-check',
                    },
                    {
                        value: 'withoutReply',
                        text: `${this.$t('campaigns.contactListing.tabs.withoutReply')}`,
                        icon: 'regular/messages-bubble-remove',
                    },
                ];
            },

            columns() {
                return [
                    {
                        name: 'name',
                        label: this.$t('campaigns.contactListing.listingHeaders.name'),
                        icon: 'regular/single-neutral',
                        className: 'flex-3',
                    },
                    {
                        name: 'phone',
                        label: this.$t('campaigns.contactListing.listingHeaders.phone'),
                        icon: 'regular/phone',
                        className: 'flex-2',
                    },
                    {
                        name: 'locale',
                        label: this.$t('campaigns.contactListing.listingHeaders.locale'),
                        icon: 'regular/earth-1',
                        className: 'flex-2',
                    },
                    {
                        name: 'sentStatus',
                        label: this.$t('campaigns.contactListing.listingHeaders.sentStatus'),
                        icon: 'regular/send-email',
                        className: 'flex-2',
                    },
                    {
                        name: 'reply',
                        label: this.$t('campaigns.contactListing.listingHeaders.reply'),
                        icon: 'regular/messages-bubble-text_1',
                        className: 'flex-2',
                    },
                    {
                        name: 'actions',
                        label: this.$t('campaigns.contactListing.listingHeaders.actions'),
                        icon: 'regular/flash-1',
                        className: 'flex-1',
                        align: 'center',
                        truncate: false,
                    },
                ];
            },

            userValidProviderPhones() {
                return this.authUser.account.userAvailableProviderPhones(this.authUser.id).length > 0;
            },
        },

        watch: {
            'authUser.account.id'(newValue) {
                if (!newValue) {
                    return;
                }

                this.$router.push({ name: 'campaigns' });
            },

            'filters.tab'() {
                this.fetchContacts();
            },

            'filters.search'() {
                this.$wait.start('fetching.campaignContacts');

                this.debounceFetchContacts();
            },

            contacts() {
                this.selections = [];
            },

            selections(newValue) {
                if (!this.contacts.length) {
                    return;
                }

                if (newValue.length) {
                    this.toDelete = null;
                }

                const contacts = this.contacts.filter(c => newValue.includes(c.id));

                this.selectedContacts = contacts;
            },

            toDelete(newValue) {
                if (!empty(newValue)) {
                    this.selections = [];
                }
            },

            selectedCampaign(newValue) {
                if (!newValue.id) {
                    return;
                }

                // Redirect if campaign does not belong to the current account
                if (newValue.account_id != this.authUser.account.id) {
                    this.$router.replace({ name: 'campaigns' });
                }
            },
        },

        methods: {
            ...mapActions('campaign', [
                'getCampaign',
                'getCampaignContacts',
                'deleteCampaign',
            ]),

            ...mapMutations('campaign', [
                'clearSelectedCampaign',
            ]),

            triggerEdit() {
                this.$modal.open('campaign', {
                    campaign: this.selectedCampaign,
                });
            },

            triggerDeleteContact(contact) {
                this.toDelete = contact;
                this.modals.removeContactOpened = true;
            },

            async deleteSelected() {
                const contacts = this.toDelete ? [this.toDelete] : this.selectedContacts;
                const ids = contacts.map(c => c.id);

                try {
                    await this.$axios.delete(`campaigns/${this.selectedCampaign.id}/contacts/${ids.join()}`);
                } catch (e) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$tc('campaigns.contactListing.errors.deleting', contacts.length),
                    });
                }

                this.fetchContacts();
                this.reset();
            },

            promptDeleteCampaign() {
                this.modals.deleteCampaignOpened = true;
            },

            async triggerDeleteCampaign() {
                try {
                    await this.deleteCampaign(this.$route.params.id);

                    this.$notify.show({
                        type: 'success',
                        message: this.$tc('campaigns.campaignModal.successes.deleted', 1),
                    });

                    this.$router.push({ name: 'campaigns' });
                } catch (e) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$tc('campaigns.campaignModal.errors.deleting', 1),
                    });
                }
            },

            triggerRemoveContacts() {
                this.modals.removeContactOpened = true;
            },

            triggerSendCampaign() {
                this.$modal.open('sendMassSms');
            },

            reset() {
                this.selections = [];
                this.toDelete = null;
            },

            async fetch() {
                this.$wait.start('fetching.campaign');

                await Promise.all([
                    this.getCampaign(this.$route.params.id),
                    this.fetchContacts(),
                ]);

                this.$wait.end('fetching.campaign');
            },

            async fetchContacts() {
                this.$wait.start('fetching.campaignContacts');

                const response = await this.getCampaignContacts({
                    campaignId: this.$route.params.id,
                    search: this.filters.search,
                    sent: this.sentFilter,
                    withReply: this.withReplyFilter,
                    page: this.pagination.currentPage,
                    perPage: this.pagination.perPage,
                });

                this.contacts = response.data;

                this.pagination.from = response.meta.from;
                this.pagination.to = response.meta.to;
                this.pagination.total = response.meta.total;
                this.pagination.lastPage = response.meta.last_page;

                this.$wait.end('fetching.campaignContacts');
            },

            scrollTop() {
                if (this.$refs.body) {
                    this.$refs.body.$el.scrollTop = 0;
                }
            },

            onPageChanged(page) {
                this.pagination.currentPage = page;

                this.$nextTick(() => {
                    this.fetchContacts();
                    this.scrollTop();
                });
            },

            onMessageSent(contactId) {
                this.contacts.forEach(contact => {
                    if (contact.id == contactId) {
                        contact.message_sent = true;
                    }
                });
            },

            onReplyReceived({ contactId, reply }) {
                this.contacts.forEach(contact => {
                    if (contact.id == contactId) {
                        contact.reply = reply;
                    }
                });
            },

            onContactLinked({ contact, campaign }) {
                const isSelectedCampaign = campaign.id == this.selectedCampaign.id;
                const hasMultiplePage = this.pagination.lastPage > 1;
                const contactAlreadyExists = this.contacts.some(c => c.id == contact.id);

                if (isSelectedCampaign && !hasMultiplePage && !contactAlreadyExists) {
                    this.contacts.push(contact);
                }
            },

            async debounceFetchContacts() {
                await this.fetchContacts();
            },

            missingValidProviderPhoneTooltip() {
                return this.userValidProviderPhones ? '' : this.$t('global.disabled.missingValidProviderPhoneTooltip');
            },
        },

        created() {
            this.fetch();

            this.debounceFetchContacts = debounce(this.debounceFetchContacts, 300);

            this.$bus.on('campaign.contactLinked', this.onContactLinked);
            this.$bus.on('campaign.messageSent', this.onMessageSent);
            this.$bus.on('campaign.replyReceived', this.onReplyReceived);
        },

        beforeRouteLeave(to, from, next) {
            if (to.name != 'campaigns.import.upload') {
                this.clearSelectedCampaign();
            }

            next();
        },

        beforeDestroy() {
            this.$bus.off('campaign.contactLinked', this.onContactLinked);
            this.$bus.off('campaign.messageSent', this.onMessageSent);
            this.$bus.off('campaign.replyReceived', this.onReplyReceived);
        },
    };
</script>
